import { useQuery, type QueryKey, keepPreviousData } from '@tanstack/react-query';

import { findInObject } from '@/utils/findInObject';
import { unifiedApiKeys } from '../keys';
import { openapiClient } from '@/lib/openapi-fetch';

import type { Cluster } from '@/types/mercury-data-types/clusters';
import type { UnifiedApiParams } from '@/types/mercury-data-types/unifiedapi';

const ENDPOINT = '/cluster/articles';

const BASE_PATHS = {
  COMMON: 'common',
  STRUCTURED: 'data.structured',
  CATALOG: 'catalog',
  TRANSLATIONS: 'translations',
} as const;

const CLUSTERS_PATHS = {
  ID: ['cluster_id'],
  TITLE: [`${BASE_PATHS.TRANSLATIONS}.en.data.structure.title`, `${BASE_PATHS.STRUCTURED}.title`],
  TEXT: [`${BASE_PATHS.STRUCTURED}.summary`],
  FIRST_SEEN: [`${BASE_PATHS.STRUCTURED}.firstSeen`],
  LAST_SEEN: [`${BASE_PATHS.STRUCTURED}.lastSeen`],
  TIMESTAMP: [`${BASE_PATHS.STRUCTURED}.timestamp`],
  CENTROID_EVENT_TIMESTAMP: ['centroid_event_timestamp'],
  TRENDS_SCORE: ['trend_score'],
  TOTAL_ARTICLES: ['articles'],
  CATEGORY: [`${BASE_PATHS.CATALOG}.category`],
  INDUSTRY: [`${BASE_PATHS.CATALOG}.industrysector`],
  COUNTRY: [`${BASE_PATHS.CATALOG}.country`],
  CATALOG: [BASE_PATHS.CATALOG],
};

export type NormalizedCluster = ReturnType<typeof normalizeCluster>;

const normalizeCluster = (data: Cluster) => ({
  id: findInObject(data, CLUSTERS_PATHS.ID),
  title: findInObject(data, CLUSTERS_PATHS.TITLE),
  text: findInObject(data, CLUSTERS_PATHS.TEXT),
  firstSeen: findInObject(data, CLUSTERS_PATHS.FIRST_SEEN),
  lastSeen: findInObject(data, CLUSTERS_PATHS.LAST_SEEN),
  timeStamp: findInObject(data, CLUSTERS_PATHS.TIMESTAMP),
  centroidEventTimestamp: findInObject(data, CLUSTERS_PATHS.CENTROID_EVENT_TIMESTAMP),
  trendsScore: findInObject(data, CLUSTERS_PATHS.TRENDS_SCORE, 0),
  totalArticles: findInObject(
    data,
    CLUSTERS_PATHS.TOTAL_ARTICLES,
    [],
    (articles) => articles.length
  ),
  totalSources: findInObject(
    data,
    CLUSTERS_PATHS.TOTAL_ARTICLES,
    [],
    (sources) => Array.from(new Set(sources.flatMap((a: any) => a.source))).length
  ),
  category: findInObject(data, CLUSTERS_PATHS.CATEGORY, ['N/A'], (c) => (c.length ? c[0] : 'N/A')),
  industry: findInObject(data, CLUSTERS_PATHS.INDUSTRY),
  country: findInObject(data, CLUSTERS_PATHS.COUNTRY),
  catalog: findInObject(data, CLUSTERS_PATHS.CATALOG),
  articles: data.articles,
});

const baseQuery = (
  params: Partial<UnifiedApiParams>,
  queryKey: QueryKey,
  usePlaceholderData?: boolean
) =>
  useQuery({
    queryKey,
    queryFn: () =>
      openapiClient.GET(ENDPOINT, {
        params: {
          query: {
            ...params,
            order: '-trending',
          },
        },
      }),
    select: (data) => ({
      count: data.data?.count,
      records: data.data?.records.map(normalizeCluster),
      pages: Math.ceil((data.data?.count ?? 0) / (params.length ?? 1)),
    }),
    placeholderData: usePlaceholderData ? keepPreviousData : undefined,
  });

export const useClusters = (params: Partial<UnifiedApiParams>, usePlaceholderData?: boolean) => {
  const list = () =>
    baseQuery(params, unifiedApiKeys.records(ENDPOINT, params), usePlaceholderData);
  const summary = () => baseQuery(params, unifiedApiKeys.summary(ENDPOINT, params), true);

  return {
    summary,
    list,
  };
};
